import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {XxSidecarPlaygroundService} from '../../../service/xx-sidecar-playground.service';

/**
 * Dynamic column descriptor for one INTERNAL typed person-data field.
 *
 * `key` is a property on a `xx.XxPersonData` row — either a fixed core field
 * (e.g. `firstName`) or a generic-attribute value keyed `gen_<typeFieldId>`
 * (e.g. `gen_7`). `header` is the German display label derived from the key.
 *
 * @ticket TM-1000001960
 */
interface XxPersonDataColumn {
    /** Row property key, e.g. `firstName` or `gen_7` */
    key: string;
    /** Column header (German label) */
    header: string;
}

/**
 * Read-side demonstration component for the Clean-Core INTERNAL typed side-car
 * (TM-1000001960).
 *
 * The component renders `xx.XxPersonData` rows for a selected XxPersonType. In
 * contrast to the EXTERNAL widgets (#4 / #5), the internal typed pattern stores
 * its generic-attribute values directly on the host data read, surfacing them
 * as `gen_<typeFieldId>` fields on each row. Because those columns are
 * RUNTIME-DYNAMIC (different person types carry different generic fields), the
 * table columns are derived from the keys present on the returned rows — so the
 * fixed name/type fields AND every `gen_<id>` value appear automatically.
 *
 * Mirrors {@link XxPersonExternalFlatComponent}: a self-contained, read-only
 * DISPLAY COMPONENT (NOT a routed page, NOT a WidgetFactory `ql` data
 * provider). Consumes {@link XxSidecarPlaygroundService#loadPersonData} for all
 * data access (never a direct HttpClient call). German UI with loading / empty
 * / error states; NULL values render as an em dash.
 *
 * @ticket TM-1000001960
 */
@Component({
    selector: 'mvs-xx-person-data',
    templateUrl: './xx-person-data.component.html',
    styleUrls: ['./xx-person-data.component.scss'],
    standalone: false
})
export class XxPersonDataComponent implements OnInit, OnChanges, OnDestroy {

    /** True once the type selector list has finished its first load. */
    initialized = false;

    /** Available person types for the type selector. */
    personTypes: any[] = [];

    /** Currently selected person type (filters the data read). */
    selectedType: any | null = null;

    /** Dynamic columns derived from the returned row keys. */
    columns: XxPersonDataColumn[] = [];

    /** Person-data rows (core fields + `gen_<id>` values). */
    rows: any[] = [];

    /** True while the data read is running. */
    loading = false;

    /** Holds a user-facing German error message, or null when there is none. */
    errorMessage: string | null = null;

    /**
     * German labels for the well-known core/host fields. Any key not listed
     * here (notably `gen_<id>`) falls back to a derived label.
     */
    private readonly coreFieldLabels: { [key: string]: string } = {
        id: 'ID',
        firstName: 'Vorname',
        lastName: 'Nachname',
        typeId: 'Personentyp-ID',
        typeName: 'Personentyp'
    };

    /** Keys that never become a visible column (internal / noise). */
    private readonly hiddenKeys = new Set<string>(['id']);

    constructor(private playgroundService: XxSidecarPlaygroundService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     *
     * @ticket TM-1000001960
     */
    initComponent(): void {
        this.refreshComponent();
    }

    /**
     * Refresh Component: (re)load the type selector list.
     *
     * @ticket TM-1000001960
     */
    refreshComponent(): void {
        this.loadTypes();
    }

    /**
     * Process changes within Binding.
     *
     * @param changes the bound input changes
     * @ticket TM-1000001960
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    /**
     * Destroy component.
     *
     * @ticket TM-1000001960
     */
    ngOnDestroy(): void {
    }

    /**
     * Load the XxPersonTypes that populate the selector.
     *
     * @ticket TM-1000001960
     */
    loadTypes(): void {
        this.errorMessage = null;
        this.playgroundService.loadXxPersonTypes().subscribe({
            next: (types) => {
                this.personTypes = types ?? [];
                this.initialized = true;
            },
            error: () => {
                this.errorMessage = 'Die Personentypen konnten nicht geladen werden.';
                this.initialized = true;
            }
        });
    }

    /**
     * Selection handler: load the internal typed person-data rows for the chosen
     * type, then derive the visible columns from the keys present on the rows
     * (so the fixed name/type fields and every `gen_<id>` value appear).
     *
     * @ticket TM-1000001960
     */
    onTypeSelect(): void {
        this.columns = [];
        this.rows = [];
        this.errorMessage = null;

        if (!this.selectedType) {
            return;
        }

        const typeId = this.selectedType.id;
        this.loading = true;

        this.playgroundService.loadPersonData(typeId).subscribe({
            next: (rows) => {
                this.rows = rows ?? [];
                this.columns = this.buildColumns(this.rows);
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.errorMessage = 'Die internen typisierten Personendaten konnten nicht geladen werden.';
            }
        });
    }

    /**
     * Derive the visible columns from the union of keys across all returned
     * rows. Core fields are ordered first (id excluded), `gen_<id>` fields keep
     * their natural order; every key gets a German header.
     *
     * @ticket TM-1000001960
     */
    private buildColumns(rows: any[]): XxPersonDataColumn[] {
        const seen = new Set<string>();
        const orderedKeys: string[] = [];

        for (const row of rows ?? []) {
            for (const key of Object.keys(row ?? {})) {
                if (this.hiddenKeys.has(key) || seen.has(key)) {
                    continue;
                }
                seen.add(key);
                orderedKeys.push(key);
            }
        }

        const coreKeys = orderedKeys.filter(k => !k.startsWith('gen_'));
        const genKeys = orderedKeys.filter(k => k.startsWith('gen_'));

        return [...coreKeys, ...genKeys].map(key => ({
            key,
            header: this.resolveHeader(key)
        }));
    }

    /**
     * German column header for a row key: a fixed label for known core fields,
     * a derived "Feld <id>" label for `gen_<id>` keys, otherwise the raw key.
     *
     * @ticket TM-1000001960
     */
    private resolveHeader(key: string): string {
        if (this.coreFieldLabels[key]) {
            return this.coreFieldLabels[key];
        }
        if (key.startsWith('gen_')) {
            return `Feld ${key.substring('gen_'.length)}`;
        }
        return key;
    }

    /**
     * Section title labelled by the selected type (per spec §3).
     * Example: Interne typisierte Daten — Antragsteller.
     *
     * @ticket TM-1000001960
     */
    get sectionTitle(): string {
        if (!this.selectedType) {
            return 'Interne typisierte Daten';
        }
        return `Interne typisierte Daten — ${this.resolveTypeLabel(this.selectedType)}`;
    }

    /**
     * Human-readable label for a person type: prefer `name`, fall back to
     * `description`, then the id.
     *
     * @ticket TM-1000001960
     */
    resolveTypeLabel(type: any): string {
        if (!type) {
            return '';
        }
        if (type.name) {
            return String(type.name);
        }
        if (type.description) {
            return String(type.description);
        }
        return `Typ ${type.id}`;
    }

    /**
     * Render a person-data value for the table cell. NULL / undefined values
     * render as an em dash so absent fields are visible.
     *
     * @ticket TM-1000001960
     */
    displayValue(row: any, key: string): string {
        const value = row?.[key];
        if (value === null || value === undefined || value === '') {
            return '—';
        }
        return String(value);
    }
}
